import React, { type PropsWithChildren } from "react";
import { useTranslate, useGo, useNavigation, useList } from "@refinedev/core";
import { CreateButton, DeleteButton, EditButton } from "@refinedev/mui";
import { useMaterialReactTable, MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { RefineListView } from "../../components";
import { useLocation } from "react-router";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { IDeal } from "@/utils/zod/dealFormValidation";
import { formatFileType } from "@/utils/formattingFunctions";
import CustomChip from "@/components/override/Chip";
import { useTableState } from "@/hooks/useTableState";

export const DealList = ({ children }: PropsWithChildren) => {
    const t = useTranslate();
    const { createUrl } = useNavigation();
    const go = useGo();
    const { pathname } = useLocation();
    const { tableState, onColumnFiltersChange, onGlobalFilterChange, onSortingChange } = useTableState<IDeal>();

    const columns = React.useMemo<MRT_ColumnDef<IDeal>[]>(
        () => [
            {
                id: "deal",
                header: "Deal Information",
                columns: [
                    {
                        id: "name",
                        header: "Deal Name",
                        accessorKey: "name",
                        size: 200,
                        Cell: ({ row }) => (
                            <Box>
                                <Typography variant="body1" fontWeight="bold">
                                    {row.original.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {row.original.description}
                                </Typography>
                            </Box>
                        ),
                    },
                ],
            },
            {
                id: "products_collections",
                header: "Products & Collections",
                columns: [
                    {
                        id: "products",
                        header: "Products",
                        accessorKey: "products",
                        size: 240,
                        Cell: ({ row }) => {
                            const MAX_IMAGES = 5;
                            const products = row.original.products;
                            const displayedProducts = products.slice(0, MAX_IMAGES);
                            const remainingCount = Math.max(0, products.length - MAX_IMAGES);

                            return (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(5, 30px)",
                                            gap: 0.5,
                                        }}
                                    >
                                        {displayedProducts.map((product) => (
                                            <Tooltip
                                                key={product.id}
                                                title={product.name}
                                                placement="top"
                                            >
                                                <Box
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: 1,
                                                        overflow: "hidden",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                    }}
                                                >
                                                    <Box
                                                        component={formatFileType(product.medias[0].type)}
                                                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        src={product.medias[0].cdn_url}
                                                        alt={product.name}
                                                        controls={false}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        ))}
                                    </Box>
                                    {remainingCount > 0 && (
                                        <Tooltip title="Click to see all products">
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    fontSize: '0.75rem',
                                                    color: 'text.secondary',
                                                    cursor: 'pointer',
                                                    ml: 1,
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                    }
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const expandedState = table.getState().expanded as Record<string, boolean>;
                                                    const isCurrentlyExpanded = expandedState[row.id] ?? false;
                                                    table.setExpanded({ [row.id]: !isCurrentlyExpanded });
                                                }}
                                            >
                                                +{remainingCount} more
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        },
                    },
                    {
                        id: "collections",
                        header: "Collections",
                        accessorKey: "collections",
                        size: 105,
                        Cell: ({ row }) => (
                            <Typography>
                                {row.original.collections.length} Collection
                            </Typography>
                        ),
                    },
                ],
            },
            {
                id: "rules",
                header: "Rules Configuration",
                columns: [
                    {
                        id: "tag_rules",
                        header: "Tag Rules",
                        accessorKey: "tag_rules",
                        size: 400,
                        Cell: ({ row }) => {
                            const MAX_RULES = 1;
                            const rules = row.original.tag_rules;
                            const displayedRules = rules?.slice(0, MAX_RULES);
                            const remainingCount = Math.max(0, rules?.length ?? 0 - MAX_RULES);

                            return (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                    {displayedRules?.map((rule, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                alignItems: "center",
                                                p: 0.5,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Box flex={1}>
                                                <Typography variant="subtitle2">
                                                    {rule.company_category || "All Categories"}
                                                </Typography>
                                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                    {rule.cost_price_upcharge_percentage !== null && (
                                                        <Chip
                                                            label={`${rule.cost_price_upcharge_percentage}%`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {rule.discount && (
                                                        <Chip
                                                            label={rule.discount.discount_string}
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                            sx={{ maxWidth: 250 }}

                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}
                                    {remainingCount > 0 && (
                                        <Tooltip title="Click to see all rules">
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    fontSize: '0.75rem',
                                                    color: 'text.secondary',
                                                    cursor: 'pointer',
                                                    ml: 1,
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                    }
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const expandedState = table.getState().expanded as Record<string, boolean>;
                                                    const isCurrentlyExpanded = expandedState[row.id] ?? false;
                                                    table.setExpanded({ [row.id]: !isCurrentlyExpanded });
                                                }}
                                            >
                                                +{remainingCount} more
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        },
                    },
                ],
            },
            {
                id: "actions",
                header: "Actions",
                size: 100,
                Cell: ({ row }) => (
                    <Stack direction="row" spacing={1}>
                        <EditButton
                            hideText
                            resource="deals"
                            recordItemId={row.original.name}
                            variant="text"
                        />
                        <DeleteButton
                            hideText
                            resource="deals"
                            recordItemId={row.original.name}
                            variant="text"
                        />
                    </Stack>
                ),
            },
        ],
        [go],
    );

    const { data, isLoading } = useList<IDeal>({
        resource: "deals",
        pagination: { pageSize: 10 },
    });

    const table = useMaterialReactTable({
        columns,
        data: data?.data ?? [],
        state: {
            ...tableState,
            isLoading,
        },
        onColumnFiltersChange,
        onGlobalFilterChange,
        onSortingChange,
        enableRowSelection: true,
        enableMultiRowSelection: true,
        enableColumnResizing: true,
        enableColumnPinning: true,
        enableFullScreenToggle: true,
        enableDensityToggle: true,
        enableGlobalFilterModes: true,
        muiTableBodyRowProps: { hover: true },
        renderDetailPanel: ({ row }) => (
            <Box
                sx={{
                    p: 2,
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 4,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Products</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {row.original.products.map((product) => (
                            <Box key={product.id} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                                <Box
                                    component={formatFileType(product.medias[0].type)}
                                    sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
                                    src={product.medias[0].cdn_url}
                                    alt={product.name}
                                    controls={false}
                                />
                                <Typography noWrap sx={{ flex: 1 }}>{product.name}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Collections</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {row.original.collections.map((collection) => (
                            <Box key={collection.id} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                                <CustomChip label={collection.name} type="collection" status="neutral" />
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Tag Rules</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {row.original.tag_rules?.map((rule, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {rule.company_category || "All Categories"}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {rule.cost_price_upcharge_percentage !== null && (
                                        <Chip
                                            label={`Price Upcharge: ${rule.cost_price_upcharge_percentage}%`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {/* {rule.discount && (
                                        <Chip
                                            label={rule.discount.discount_string}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )} */}
                                </Box>

                                {rule.discount && (
                                    <Box sx={{ pl: 1 }}>
                                        {/* {rule.discount.discount_type === "percentage" && rule.discount.percentage_tiers.map((tier, i) => (
                                            <Typography key={i} variant="body2" color="text.secondary">
                                                Buy {tier.quantity}+ get {tier.value}% off
                                            </Typography>
                                        ))}
                                        {rule.discount.discount_type === "fixed" && rule.discount.fixed_tiers.map((tier, i) => (
                                            <Typography key={i} variant="body2" color="text.secondary">
                                                Buy {tier.quantity}+ get ₹{tier.value} off
                                            </Typography>
                                        ))}
                                        {rule.discount.discount_type === "bundle" && rule.discount.bundle_tiers.map((tier, i) => (
                                            <Typography key={i} variant="body2" color="text.secondary">
                                                Buy {tier.quantity} for ₹{tier.value} each
                                            </Typography>
                                        ))} */}
                                        {rule.discount.discount_type === "product_creation_based" && (
                                            <Typography key={index} variant="body2" color="text.secondary">
                                                {rule.discount.discount_string}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        ),
        initialState: {
            density: "compact",
            columnPinning: {
                left: ["deal.id", "deal.name"],
                right: ["actions"],
            },
            columnVisibility: {
                "products_collections.collections": false,
            },
        },
    });

    return (
        <>
            <RefineListView
                headerButtons={(props) => [
                    <CreateButton
                        {...props.createButtonProps}
                        key="create"
                        size="medium"
                        sx={{ height: "40px" }}
                        onClick={() => {
                            return go({
                                to: `${createUrl("deals")}`,
                                query: {
                                    to: pathname,
                                },
                                options: {
                                    keepQuery: true,
                                },
                                type: "replace",
                            });
                        }}
                    >
                        {t("deals.actions.add")}
                    </CreateButton>,
                ]}
            >
                <MaterialReactTable table={table} />
            </RefineListView>
            {children}
        </>
    );
}; 